import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { LoadingState } from '@/components/AsyncState';
import { loginRedirectPath } from '@/lib/navigation';
import '@/index.css';

const AppShell = lazy(() => import('@/components/AppShell'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const IssuesPage = lazy(() => import('@/pages/IssuesPage'));
const CreateIssuePage = lazy(() => import('@/pages/CreateIssuePage'));
const IssueDetailPage = lazy(() => import('@/pages/IssueDetailPage'));
const GaiaPage = lazy(() => import('@/pages/GaiaPage'));
const CctvPage = lazy(() => import('@/pages/CctvPage'));
const EntertainmentPage = lazy(() => import('@/pages/EntertainmentPage'));
const IncidentReportsPage = lazy(() => import('@/pages/IncidentReportsPage'));
const IncidentReportFormPage = lazy(() => import('@/pages/IncidentReportFormPage'));
const IncidentReportDetailPage = lazy(() => import('@/pages/IncidentReportDetailPage'));

function PageLoading() {
  return <div className="route-loading"><LoadingState label="Opening Operations…" /></div>;
}

function Protected({ children }) {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (!user) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?${new URLSearchParams({ next })}`} replace />;
  }
  return children;
}

function LoginRoute() {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (user) return <Navigate to={loginRedirectPath(location.search)} replace />;
  return <LoginPage />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/change-password"
          element={<Protected><ChangePasswordPage /></Protected>}
        />

        <Route element={<Protected><AppShell /></Protected>}>
          <Route index element={<Navigate to="/issues" replace />} />
          <Route path="/issues" element={<IssuesPage />} />
          <Route path="/issues/new" element={<CreateIssuePage />} />
          <Route path="/issues/:issueId" element={<IssueDetailPage />} />
          <Route path="/incident-reports" element={<IncidentReportsPage />} />
          <Route path="/incident-reports/new" element={<IncidentReportFormPage />} />
          <Route path="/incident-reports/:reportId" element={<IncidentReportDetailPage />} />
          <Route path="/incident-reports/:reportId/edit" element={<IncidentReportFormPage />} />
          <Route path="/cctv" element={<CctvPage />} />
          <Route path="/entertainment" element={<EntertainmentPage />} />
          <Route path="/gaia" element={<GaiaPage />} />
          <Route path="/dashboard" element={<Navigate to="/issues" replace />} />
          <Route path="/tasks/*" element={<Navigate to="/issues" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/issues" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          richColors
          toastOptions={{
            style: {
              background: '#171111',
              border: '1px solid #382323',
              color: '#f0f0f1',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
